import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFirestore, collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Swal from 'sweetalert2';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './Reservations.css';

const db = getFirestore();
const functions = getFunctions();

const STATUS_LABELS = {
  pending: 'Pending confirmation',
  confirmed: 'Confirmed',
  seated: 'Seated',
  completed: 'Completed',
  cancelled: 'Cancelled',
  no_show: 'No-show',
};

/** "My Reservations" — upcoming and past, with confirm/cancel actions. */
const MyReservationsPage = () => {
  const navigate = useNavigate();
  const { user, userCollection, loading: authLoading } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('upcoming');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const q = query(
        collection(db, userCollection, user.uid, 'reservations'),
        orderBy('dateTime', 'desc'),
        limit(100)
      );
      const snap = await getDocs(q);
      // When the signed-in user is ALSO a merchant, their
      // users/{uid}/reservations path is the merchant's CANONICAL collection
      // (every customer's bookings) — keep only docs that belong to this
      // consumer: their own customerUid, or mirror docs (which always carry
      // a `reservationId` field that canonical docs don't have).
      setReservations(
        snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((r) => r.customerUid === user.uid || (r.customerUid == null && r.reservationId != null))
      );
    } catch (e) {
      console.error('Error loading reservations', e);
    }
    setLoading(false);
  }, [user, userCollection]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/');
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const isUpcoming = (r) => {
    if (!['pending', 'confirmed', 'seated'].includes(r.status)) return false;
    const dt = r.dateTime?.toDate?.();
    if (!dt) return false;
    return dt > new Date(Date.now() - 6 * 3600 * 1000);
  };

  const updateStatus = async (r, status) => {
    setBusyId(r.id);
    try {
      const fn = httpsCallable(functions, 'updateReservationStatus');
      const { data } = await fn({
        businessId: r.businessId,
        locationId: r.locationId,
        reservationId: r.id,
        status,
      });
      if (data.refunded) {
        await Swal.fire({
          icon: 'success',
          title: 'Cancelled & refunded',
          text: 'Your card was fully refunded.',
          confirmButtonColor: '#ff5722',
        });
      }
      await load();
    } catch (e) {
      Swal.fire('Error', e.message || 'Something went wrong', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const cancelWithConfirm = async (r) => {
    const prepaid = r.paymentStatus === 'prepaid';
    const result = await Swal.fire({
      title: 'Cancel reservation?',
      text: prepaid
        ? 'This reservation has a prepaid pre-order. Cancelling now will fully refund your card.'
        : 'Are you sure you want to cancel this reservation?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, cancel it',
      cancelButtonText: 'Keep it',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
    });
    if (result.isConfirmed) await updateStatus(r, 'cancelled');
  };

  const upcoming = reservations
    .filter(isUpcoming)
    .sort((a, b) => (a.dateTime?.toMillis?.() || 0) - (b.dateTime?.toMillis?.() || 0));
  const past = reservations.filter((r) => !isUpcoming(r));
  const items = tab === 'upcoming' ? upcoming : past;

  return (
    <>
      <Header />
      <div className="ff-resv-page">
        <h2 style={{ marginBottom: 16 }}>My Reservations</h2>
        <div className="ff-resv-tabs">
          <button className={`ff-resv-tab ${tab === 'upcoming' ? 'active' : ''}`} onClick={() => setTab('upcoming')}>
            Upcoming ({upcoming.length})
          </button>
          <button className={`ff-resv-tab ${tab === 'past' ? 'active' : ''}`} onClick={() => setTab('past')}>
            Past
          </button>
        </div>

        {loading ? (
          <p style={{ textAlign: 'center', color: '#6b7280' }}>Loading…</p>
        ) : items.length === 0 ? (
          <div className="ff-resv-card" style={{ textAlign: 'center', padding: 40 }}>
            <i className="fas fa-calendar-times" style={{ fontSize: 40, color: '#d1d5db' }}></i>
            <p style={{ color: '#6b7280', marginTop: 12 }}>
              {tab === 'upcoming' ? 'No upcoming reservations' : 'No past reservations'}
            </p>
          </div>
        ) : (
          items.map((r) => {
            const dt = r.dateTime?.toDate?.();
            const busy = busyId === r.id;
            return (
              <div key={r.id} className="ff-resv-card ff-resv-item" onClick={() => navigate(`/reservation/${r.id}`)}>
                <div className="ff-resv-item-head">
                  <h3 className="ff-resv-item-name">{r.locationName || 'Restaurant'}</h3>
                  <span className={`ff-resv-status ${r.status}`}>{STATUS_LABELS[r.status] || r.status}</span>
                </div>
                <div className="ff-resv-item-meta">
                  <span>
                    <i className="fas fa-calendar" style={{ marginRight: 6, color: '#ff5722' }}></i>
                    {dt ? dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : ''}
                    {' · '}
                    {dt ? dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : ''}
                  </span>
                  <span>
                    <i className="fas fa-users" style={{ marginRight: 6, color: '#ff5722' }}></i>
                    {r.partySize} guests
                  </span>
                  {r.paymentStatus === 'prepaid' && (
                    <span style={{ color: '#15803d', fontWeight: 600 }}>
                      <i className="fas fa-check-circle" style={{ marginRight: 4 }}></i>Prepaid
                    </span>
                  )}
                </div>
                {tab === 'upcoming' && ['pending', 'confirmed'].includes(r.status) && (
                  <div className="ff-resv-item-actions" onClick={(e) => e.stopPropagation()}>
                    {r.status === 'pending' && (
                      <button className="ff-resv-btn-confirm" disabled={busy} onClick={() => updateStatus(r, 'confirmed')}>
                        {busy ? '…' : 'Confirm'}
                      </button>
                    )}
                    <button className="ff-resv-btn-cancel" disabled={busy} onClick={() => cancelWithConfirm(r)}>
                      {busy ? '…' : 'Cancel'}
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
      <Footer />
    </>
  );
};

export default MyReservationsPage;
